import * as THREE from 'three'

/**
 * Sample a set of meshes' vertices into world space (strided so the total stays near `maxPoints`), for
 * principal-axis analysis. `toWorld` maps a mesh to the world matrix its vertices should be transformed by.
 */
function sampleMeshPoints(
  meshes: THREE.Mesh[],
  maxPoints: number,
  toWorld: (mesh: THREE.Mesh) => THREE.Matrix4,
): THREE.Vector3[] {
  let total = 0
  for (const m of meshes) {
    total += (m.geometry as THREE.BufferGeometry).attributes.position?.count ?? 0
  }
  if (total === 0) return []
  const stride = Math.max(1, Math.floor(total / maxPoints))
  const points: THREE.Vector3[] = []
  for (const m of meshes) {
    const pos = (m.geometry as THREE.BufferGeometry).attributes.position as
      | THREE.BufferAttribute
      | undefined
    if (!pos) continue
    const world = toWorld(m)
    for (let i = 0; i < pos.count; i += stride) {
      points.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(world))
    }
  }
  return points
}

/**
 * Dominant direction of a point cloud (eigenvector of the largest covariance eigenvalue) via power
 * iteration — a part's true long axis, independent of how it's oriented in the model.
 */
export function principalAxis(points: THREE.Vector3[], centroid: THREE.Vector3): THREE.Vector3 {
  let xx = 0
  let xy = 0
  let xz = 0
  let yy = 0
  let yz = 0
  let zz = 0
  for (const p of points) {
    const dx = p.x - centroid.x
    const dy = p.y - centroid.y
    const dz = p.z - centroid.z
    xx += dx * dx
    xy += dx * dy
    xz += dx * dz
    yy += dy * dy
    yz += dy * dz
    zz += dz * dz
  }
  const v = new THREE.Vector3(1, 0.7, 0.4).normalize()
  for (let i = 0; i < 24; i++) {
    const nx = xx * v.x + xy * v.y + xz * v.z
    const ny = xy * v.x + yy * v.y + yz * v.z
    const nz = xz * v.x + yz * v.y + zz * v.z
    const len = Math.hypot(nx, ny, nz)
    if (len < 1e-20) break
    v.set(nx / len, ny / len, nz / len)
  }
  return v
}

export function isFastenerName(name: string): boolean {
  return (
    /\b(?:ISO|DIN|EN|NF|GB|ANSI|ASME)[ _-]?\d/i.test(name) ||
    /\b(?:screw|bolt|washer|nut|stud|rivet|dowel|pin)s?\b/i.test(name) ||
    /\bM\d+(?:\.\d+)?\s*[x×]/i.test(name)
  )
}

function covariance(points: THREE.Vector3[], centroid: THREE.Vector3): number[][] {
  let xx = 0
  let xy = 0
  let xz = 0
  let yy = 0
  let yz = 0
  let zz = 0
  for (const p of points) {
    const dx = p.x - centroid.x
    const dy = p.y - centroid.y
    const dz = p.z - centroid.z
    xx += dx * dx
    xy += dx * dy
    xz += dx * dz
    yy += dy * dy
    yz += dy * dz
    zz += dz * dz
  }
  return [
    [xx, xy, xz],
    [xy, yy, yz],
    [xz, yz, zz],
  ]
}

/** Eigenvalues and eigenvectors of a symmetric 3x3 matrix via Jacobi rotations. */
function symmetricEigen(m: number[][]): { values: number[]; vectors: THREE.Vector3[] } {
  const a = m.map((r) => r.slice())
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ]
  for (let iter = 0; iter < 50; iter++) {
    let p = 0
    let q = 1
    let max = Math.abs(a[0][1])
    if (Math.abs(a[0][2]) > max) {
      max = Math.abs(a[0][2])
      p = 0
      q = 2
    }
    if (Math.abs(a[1][2]) > max) {
      max = Math.abs(a[1][2])
      p = 1
      q = 2
    }
    if (max < 1e-12) break
    const phi = 0.5 * Math.atan2(2 * a[p][q], a[q][q] - a[p][p])
    const c = Math.cos(phi)
    const s = Math.sin(phi)
    for (let i = 0; i < 3; i++) {
      const aip = a[i][p]
      const aiq = a[i][q]
      a[i][p] = c * aip - s * aiq
      a[i][q] = s * aip + c * aiq
    }
    for (let i = 0; i < 3; i++) {
      const api = a[p][i]
      const aqi = a[q][i]
      a[p][i] = c * api - s * aqi
      a[q][i] = s * api + c * aqi
    }
    for (let i = 0; i < 3; i++) {
      const vip = v[i][p]
      const viq = v[i][q]
      v[i][p] = c * vip - s * viq
      v[i][q] = s * vip + c * viq
    }
  }
  return {
    values: [a[0][0], a[1][1], a[2][2]],
    vectors: [
      new THREE.Vector3(v[0][0], v[1][0], v[2][0]).normalize(),
      new THREE.Vector3(v[0][1], v[1][1], v[2][1]).normalize(),
      new THREE.Vector3(v[0][2], v[1][2], v[2][2]).normalize(),
    ],
  }
}

function revolutionAxis(points: THREE.Vector3[], centroid: THREE.Vector3): THREE.Vector3 {
  const { values, vectors } = symmetricEigen(covariance(points, centroid))
  const [lo, mid, hi] = [0, 1, 2].sort((i, j) => values[i] - values[j])
  return values[hi] - values[mid] >= values[mid] - values[lo] ? vectors[hi] : vectors[lo]
}

export type ExplodeInstance = { key: string; meshes: THREE.Mesh[]; elongated?: boolean }

function meshesBox(meshes: THREE.Mesh[], toWorld: (mesh: THREE.Mesh) => THREE.Matrix4): THREE.Box3 {
  const box = new THREE.Box3()
  for (const m of meshes) {
    const geometry = m.geometry as THREE.BufferGeometry
    if (!geometry.boundingBox) geometry.computeBoundingBox()
    if (geometry.boundingBox) box.union(geometry.boundingBox.clone().applyMatrix4(toWorld(m)))
  }
  return box
}

/**
 * Compute, per instance, the offset it should fly out by at full explosion (factor 1). Elongated parts
 * (`elongatedRatio` aspect or more) slide along their own principal axis, head-first; compact parts push
 * radially from the assembly centre. The travel distance is uniform across the group so no part flies far
 * just because it happens to be long. `toWorld` maps a mesh to its base world matrix.
 */
export function computeExplodeOffsets(
  instances: ExplodeInstance[],
  toWorld: (mesh: THREE.Mesh) => THREE.Matrix4,
  elongatedRatio: number,
): Map<string, THREE.Vector3> {
  const meshesByKey = new Map(instances.map((i) => [i.key, i.meshes]))
  const data: {
    key: string
    centroid: THREE.Vector3
    axis: THREE.Vector3
    along: number
    perp: number
    mid: number
    forced: boolean
    head: number
  }[] = []

  for (const { key, meshes, elongated } of instances) {
    const points = sampleMeshPoints(meshes, 200, toWorld)
    if (points.length === 0) continue
    const centroid = points
      .reduce((acc, p) => acc.add(p), new THREE.Vector3())
      .multiplyScalar(1 / points.length)
    // Fasteners explode along their shaft (axis of revolution); everything else along its longest axis.
    const axis = elongated ? revolutionAxis(points, centroid) : principalAxis(points, centroid)
    let lo = Infinity
    let hi = -Infinity
    let perp = 0
    const proj: { a: number; r: number }[] = []
    for (const p of points) {
      const d = p.clone().sub(centroid)
      const a = d.dot(axis)
      lo = Math.min(lo, a)
      hi = Math.max(hi, a)
      const r = d.sub(axis.clone().multiplyScalar(a)).length()
      perp = Math.max(perp, r)
      proj.push({ a, r })
    }
    const span = hi - lo
    let rHi = 0
    let rLo = 0
    if (span > 1e-6) {
      const hiEdge = hi - 0.25 * span
      const loEdge = lo + 0.25 * span
      for (const { a, r } of proj) {
        if (a >= hiEdge) rHi = Math.max(rHi, r)
        if (a <= loEdge) rLo = Math.max(rLo, r)
      }
    }
    const head = Math.abs(rHi - rLo) / (perp || 1) > 0.05 ? (rHi >= rLo ? 1 : -1) : 0
    data.push({
      key,
      centroid,
      axis,
      along: hi - lo,
      perp: perp * 2,
      mid: (lo + hi) / 2,
      forced: !!elongated,
      head,
    })
  }

  const assemblyBox = new THREE.Box3()
  for (const d of data) assemblyBox.union(meshesBox(meshesByKey.get(d.key) ?? [], toWorld))
  const centre = assemblyBox.isEmpty()
    ? new THREE.Vector3()
    : assemblyBox.getCenter(new THREE.Vector3())
  const diagonal = assemblyBox.isEmpty() ? 0 : assemblyBox.getSize(new THREE.Vector3()).length()
  const baseDistance = diagonal * 0.3

  const offsets = new Map<string, THREE.Vector3>()
  for (const d of data) {
    const elongation = d.perp > 1e-6 ? d.along / d.perp : Infinity
    let dir: THREE.Vector3
    let magnitude: number
    if (d.forced) {
      // Fasteners back straight out of their hole along the shaft, head-first (wider end leads).
      // Headless parts fall back to pushing away from the centre.
      dir = d.axis.clone()
      if (d.head !== 0) {
        if (d.head < 0) dir.negate()
      } else {
        const outward = d.centroid.clone().sub(centre).dot(dir)
        if (outward < 0) dir.negate()
        else if (Math.abs(outward) < 1e-6 && dir.y < 0) dir.negate()
      }
      // Travel scales with the screw's own length (a long bolt pulls out further than a short one),
      // clamped to the group's base distance so nothing shoots off. 1.5x clears the hole with a gap.
      const travel = Math.max(d.along * 1.5, d.perp)
      magnitude = baseDistance > 1e-6 ? Math.min(baseDistance, travel) : travel
    } else if (elongation >= elongatedRatio) {
      dir = d.axis.clone()
      const asymmetry = d.along > 1e-6 ? Math.abs(2 * d.mid) / d.along : 0
      if (asymmetry > 0.06) {
        if (d.mid > 0) dir.negate()
      } else {
        const along = d.centroid.clone().sub(centre).dot(dir)
        if (Math.abs(along) > 1e-6) {
          if (along < 0) dir.negate()
        } else if (dir.y < 0) {
          dir.negate()
        }
      }
      magnitude = baseDistance > 1e-6 ? Math.min(d.along, baseDistance) : d.along
    } else {
      dir = d.centroid.clone().sub(centre)
      if (dir.lengthSq() < 1e-12) dir.set(0, 1, 0)
      magnitude = baseDistance > 1e-6 ? baseDistance : Math.max(d.along, d.perp, 1)
    }
    offsets.set(d.key, dir.normalize().multiplyScalar(magnitude))
  }
  return offsets
}
